import { CATALOGO, ITENS_POR_SETOR, UNIDADES } from "@caint/data";
import type { ChaveSelo } from "@caint/engine";
import type { CicloPublico } from "@caint/shared";
import type { AvaliacaoRemota } from "./api.ts";
import { mesAno, mesAnoNum, rotuloMes } from "./datas.ts";
import { notaPreenchida } from "./supervisao.ts";

/**
 * Quem ABRE/ENCERRA ciclos (gestão): presidente e admin. Os demais papéis veem
 * a lista de ciclos em leitura, mas não abrem, encerram nem alteram o estado.
 */
export function podeGerirCiclo(papel: string): boolean {
  return papel === "presidente" || papel === "admin";
}

export type StatusCicloUI = "aberto" | "encerrado";

/** Nome oficial da unidade (cadastro estático) — determinístico. */
export function nomeUnidade(unidadeId?: string): string {
  return UNIDADES.find((u) => u.id === unidadeId)?.nome ?? "Unidade";
}

/**
 * Um ciclo está de fato ABERTO quando o status é "aberto" E hoje ainda não passou
 * da `dataFim` (a janela). Antes o rótulo olhava só o status e enganava: um ciclo
 * fora da janela aparecia "aberto" mas recusava toda edição (o servidor
 * `cicloEditavel` e o `avaliacaoEditavel` do cliente já checavam a janela). Agora
 * o rótulo, o seletor e a seleção automática de ciclo refletem a janela. `agora`
 * é injetável para testes; por padrão, o relógio real.
 */
export function cicloAberto(av: AvaliacaoRemota | null | undefined, agora: number = Date.now()): boolean {
  const c = av?.ciclo;
  if (!c || c.status !== "aberto") {
    return false;
  }
  if (c.dataFim && agora > Date.parse(c.dataFim)) {
    return false; // passou da janela → efetivamente encerrado (não editável)
  }
  return true;
}

/**
 * Rótulo da FASE a partir do índice `i` (1-based) e do total `n` de ciclos da
 * unidade: um par vira "Inicial/Final"; havendo visitas ALÉM do par, numera
 * "Visita N". Derivado da ordem cronológica (doc 16) — não há enum de fase no
 * pull; a semântica de inicial/final É a ordem da visita. Pura.
 */
export function faseVisita(i: number, n: number): string {
  if (n <= 2) {
    return i === 1 ? "Inicial" : "Final";
  }
  return `Visita ${i}`;
}

/** Índice (1-based) e total de ciclos da unidade daquela avaliação (por data). */
function indiceNaUnidade(
  avaliacoes: readonly AvaliacaoRemota[],
  av: AvaliacaoRemota,
): { i: number; n: number } {
  const daUnidade = avaliacoes
    .filter((a) => a.unidadeId === av.unidadeId)
    .sort((a, b) => (a.ciclo?.dataInicio ?? "").localeCompare(b.ciclo?.dataInicio ?? ""));
  const idx = daUnidade.findIndex((a) => a.id === av.id);
  return { i: idx < 0 ? 0 : idx + 1, n: daUnidade.length };
}

export interface RotuloCiclo {
  /** L1 — nome da unidade (proeminente). */
  unidade: string;
  /** L2 — "Inicial" | "Final" | "Visita N". */
  fase: string;
  /** L2 — período "MM/AAAA" do início do ciclo. */
  periodo: string;
  status: StatusCicloUI;
  aberto: boolean;
}

/**
 * Rótulo de um ciclo (para o seletor global). Função PURA sobre `avaliacoes`
 * (precisa do conjunto para achar o índice da visita na unidade). `null` se a
 * avaliação não existir.
 */
export function rotularCiclo(
  avaliacoes: readonly AvaliacaoRemota[],
  avaliacaoId: string | null,
): RotuloCiclo | null {
  if (!avaliacaoId) {
    return null;
  }
  const av = avaliacoes.find((a) => a.id === avaliacaoId);
  if (!av) {
    return null;
  }
  const { i, n } = indiceNaUnidade(avaliacoes, av);
  // Status fiel à janela: um ciclo fora da `dataFim` (mesmo com status "aberto")
  // aparece como encerrado, coerente com a edição (não engana mais o avaliador).
  const aberto = cicloAberto(av);
  const status: StatusCicloUI = aberto ? "aberto" : "encerrado";
  return {
    unidade: nomeUnidade(av.unidadeId),
    fase: faseVisita(i, n),
    periodo: mesAno(av.ciclo?.dataInicio),
    status,
    aberto,
  };
}

export interface CicloDisponivel extends RotuloCiclo {
  avaliacaoId: string;
  unidadeId: string;
  dataInicio: string | null;
  /** Mês (1-12) e ano do início — para filtros mês/ano. `null` se sem data. */
  mes: number | null;
  ano: number | null;
}

/**
 * Lista TODOS os ciclos (avaliações) para o seletor/gestão global: abertos
 * primeiro, depois por data (mais recentes primeiro). Função PURA sobre
 * `avaliacoes`. É a mesma lista usada no desktop e no mobile.
 */
export function ciclosDisponiveis(avaliacoes: readonly AvaliacaoRemota[]): CicloDisponivel[] {
  return avaliacoes
    .map((av): CicloDisponivel | null => {
      const r = rotularCiclo(avaliacoes, av.id);
      if (!r) {
        return null;
      }
      const my = mesAnoNum(av.ciclo?.dataInicio);
      return {
        ...r,
        avaliacaoId: av.id,
        unidadeId: av.unidadeId,
        dataInicio: av.ciclo?.dataInicio ?? null,
        mes: my?.mes ?? null,
        ano: my?.ano ?? null,
      };
    })
    .filter((x): x is CicloDisponivel => x != null)
    .sort((a, b) => {
      if (a.aberto !== b.aberto) {
        return a.aberto ? -1 : 1; // abertos primeiro
      }
      return (b.dataInicio ?? "").localeCompare(a.dataInicio ?? ""); // recentes primeiro
    });
}

/* ---------------- Filtros da gestão de Ciclos (mês/ano/unidade) ---------------- */

export interface FiltroCiclos {
  ano: number | null;
  mes: number | null; // 1-12
  unidadeId: string | null;
}

export const FILTRO_CICLOS_VAZIO: FiltroCiclos = { ano: null, mes: null, unidadeId: null };

export interface OpcoesFiltro {
  anos: number[]; // desc
  meses: number[]; // 1-12 asc
  unidades: { id: string; nome: string }[]; // por nome
}

/** Opções de filtro derivadas da lista carregada (popula os selects). Pura. */
export function opcoesFiltro(ciclos: readonly CicloPublico[]): OpcoesFiltro {
  const anos = new Set<number>();
  const meses = new Set<number>();
  const unidades = new Map<string, string>();
  for (const c of ciclos) {
    const my = mesAnoNum(c.dataInicio);
    if (my) {
      anos.add(my.ano);
      meses.add(my.mes);
    }
    if (!unidades.has(c.unidadeId)) {
      unidades.set(c.unidadeId, c.unidadeNome);
    }
  }
  return {
    anos: [...anos].sort((a, b) => b - a),
    meses: [...meses].sort((a, b) => a - b),
    unidades: [...unidades.entries()]
      .map(([id, nome]) => ({ id, nome }))
      .sort((a, b) => a.nome.localeCompare(b.nome)),
  };
}

/** Filtra ciclos por ano/mês/unidade (campo `null` = "todos"). Pura. */
export function filtrarCiclos(ciclos: readonly CicloPublico[], f: FiltroCiclos): CicloPublico[] {
  return ciclos.filter((c) => {
    if (f.unidadeId && c.unidadeId !== f.unidadeId) {
      return false;
    }
    if (f.ano == null && f.mes == null) {
      return true;
    }
    const my = mesAnoNum(c.dataInicio);
    if (!my) {
      return false;
    }
    if (f.ano != null && my.ano !== f.ano) {
      return false;
    }
    if (f.mes != null && my.mes !== f.mes) {
      return false;
    }
    return true;
  });
}

/**
 * Default do ciclo ativo (3 ramos, decisão Jun/2026):
 * (a) `atual` ainda existe → mantém (restaura a persistência);
 * (b) exatamente 1 ciclo ABERTO → auto-seleciona;
 * (c) múltiplos abertos e sem persistência válida → `null` (NÃO adivinha; a UI
 *     abre o seletor / a tela de Ciclos).
 * Sem nenhum aberto → a avaliação mais recente (leitura), ou `null` se vazio.
 * Pura e determinística.
 */
export function escolherAvaliacao(
  avaliacoes: readonly AvaliacaoRemota[],
  atual: string | null,
): string | null {
  if (atual && avaliacoes.some((a) => a.id === atual)) {
    return atual;
  }
  const abertos = avaliacoes.filter(cicloAberto);
  if (abertos.length === 1) {
    return abertos[0]?.id ?? null;
  }
  if (abertos.length > 1) {
    return null; // ambíguo — não adivinha
  }
  const recente = [...avaliacoes].sort((a, b) =>
    (b.ciclo?.dataInicio ?? "").localeCompare(a.ciclo?.dataInicio ?? ""),
  )[0];
  return recente?.id ?? null;
}

/**
 * Telas cujo conteúdo É de um ciclo específico (leem/escrevem `avaliacaoId`).
 * Sem ciclo selecionado elas não têm o que mostrar e — pior — todas as escritas
 * do estado (`patch`, `editarSupervisao`, `editarEscopo`, …) fazem early-return
 * silencioso, deixando a tela parecer editável enquanto descarta tudo. O shell
 * usa isto para BLOQUEAR a tela e exigir a seleção antes de abrir (ago/2026).
 *
 * A chave é a mesma do `tab` e da `rota` empilhada — os dois compartilham o
 * namespace (ex.: "checklist" existe como aba e como rota).
 */
const TELAS_COM_CICLO: ReadonlySet<string> = new Set([
  "inicio",
  "checklist",
  "escopo",
  "risco",
  "relatorio",
  "supervisao",
  "planos",
  "imprimir-sup",
]);

/**
 * A tela exige um ciclo selecionado? Telas de cadastro/referência (ciclos,
 * usuários, catálogo, IMGR, migração…) não exigem — funcionam sem ciclo ativo.
 * Pura, para ser a ÚNICA fonte de verdade dos dois shells (mobile e desktop).
 */
export function exigeCicloSelecionado(tela: string): boolean {
  return TELAS_COM_CICLO.has(tela);
}

export interface VisitaOpcao {
  /** id da avaliação daquele ciclo (para `selecionar`). */
  id: string;
  /** Rótulo "Nª visita · mês" (mês real do início do ciclo). */
  rotulo: string;
}

/**
 * Lista as visitas (ciclos) de uma unidade, em ordem cronológica. Cada visita é
 * um ciclo separado — a Nª visita = o N-ésimo ciclo aberto para a unidade.
 */
export function visitasDaUnidade(avaliacoes: readonly AvaliacaoRemota[], unidadeId?: string): VisitaOpcao[] {
  if (!unidadeId) {
    return [];
  }
  return avaliacoes
    .filter((a) => a.unidadeId === unidadeId)
    .sort((a, b) => (a.ciclo?.dataInicio ?? "").localeCompare(b.ciclo?.dataInicio ?? ""))
    .map((a, i) => {
      const mes = rotuloMes(a.ciclo?.dataInicio);
      return { id: a.id, rotulo: `${i + 1}ª visita${mes ? ` · ${mes}` : ""}` };
    });
}

/* ---------------- Ciclos da rede (card "Unidades da rede" da Início) ---------------- */

/** Setor de cada item do catálogo — para contar o preenchimento dentro do escopo. */
const SETOR_DO_ITEM = new Map(CATALOGO.map((it) => [it.id, it.sector]));
/** Metadados de cadastro da unidade (nome/município/CNES) por id. */
const UNIDADE_POR_ID = new Map(UNIDADES.map((u) => [u.id, u]));

/**
 * Uma linha do card "Unidades da rede": um CICLO real (uma avaliação), não uma
 * unidade do cadastro. Unidade com 1ª e 2ª visita rende duas linhas.
 *
 * Os números vêm dos caches revalidados no SERVIDOR a cada push
 * (`cachesInicial`), e não de um recálculo item a item no cliente: são 350
 * itens × N ciclos da rede: recalcular tudo a cada tecla digitada no checklist
 * travaria a tela de Início. A tela sobrepõe o ciclo ATIVO com o número vivo,
 * que é o único que pode estar à frente do servidor.
 */
export interface CicloRede extends CicloDisponivel {
  municipio: string;
  cnes: string | null;
  /** IMGQ ajustado do cache do servidor. `null` ⇒ ainda sem base calculada. */
  imgq: number | null;
  /** Selo do cache do servidor. `null` ⇒ pendente. */
  selo: string | null;
  /** Itens do escopo do ciclo com maturidade inicial preenchida. */
  respondidos: number;
  /** Universo: itens do catálogo nos setores do escopo do ciclo. */
  itensEscopo: number;
  /** Preenchimento em % (0-100), arredondado. */
  preenchido: number;
}

/** Setores do escopo do ciclo. Vazio/ausente ⇒ TODOS (compat, doc 17b §3.4). */
function setoresDoCiclo(av: AvaliacaoRemota | undefined): ReadonlySet<string> | null {
  const ativos = av?.escopo?.setoresAtivos;
  return ativos && ativos.length > 0 ? new Set(ativos) : null;
}

/** Universo de itens do escopo. `null` de setores ⇒ catálogo inteiro (legado). */
function itensNoEscopo(ativos: ReadonlySet<string> | null): number {
  if (!ativos) {
    return CATALOGO.length;
  }
  let n = 0;
  for (const s of ativos) {
    n += ITENS_POR_SETOR[s] ?? 0;
  }
  return n;
}

/** Itens DENTRO do escopo que já têm nota do avaliador. */
function respondidosNoEscopo(av: AvaliacaoRemota | undefined, ativos: ReadonlySet<string> | null): number {
  let n = 0;
  for (const r of av?.respostas ?? []) {
    const setor = SETOR_DO_ITEM.get(r.itemId);
    if (setor && (!ativos || ativos.has(setor)) && notaPreenchida(r.mi)) {
      n++;
    }
  }
  return n;
}

/** Monta a linha de um ciclo a partir do rótulo + da avaliação correspondente. */
function linhaDoCiclo(c: CicloDisponivel, av: AvaliacaoRemota | undefined): CicloRede {
  const u = UNIDADE_POR_ID.get(c.unidadeId);
  const ativos = setoresDoCiclo(av);
  const itensEscopo = itensNoEscopo(ativos);
  const respondidos = respondidosNoEscopo(av, ativos);
  const caches = av?.cachesInicial ?? null;
  return {
    ...c,
    municipio: u?.municipio ?? "—",
    cnes: u?.cnes ?? null,
    imgq: caches?.imgqAjustado ?? null,
    selo: caches?.selo ?? null,
    respondidos,
    itensEscopo,
    preenchido: itensEscopo > 0 ? Math.round((respondidos / itensEscopo) * 100) : 0,
  };
}

/**
 * Os ciclos que EXISTEM de fato (uma entrada por avaliação), do mais NOVO para o
 * mais antigo. Empate de data → nome da unidade, para a ordem não oscilar entre
 * renders. Pura — o cadastro de unidades entra só como rótulo.
 */
export function ciclosDaRede(avaliacoes: readonly AvaliacaoRemota[]): CicloRede[] {
  const porId = new Map(avaliacoes.map((a) => [a.id, a]));
  return ciclosDisponiveis(avaliacoes)
    .map((c) => linhaDoCiclo(c, porId.get(c.avaliacaoId)))
    .sort((a, b) => {
      const data = (b.dataInicio ?? "").localeCompare(a.dataInicio ?? "");
      return data !== 0 ? data : a.unidade.localeCompare(b.unidade, "pt-BR");
    });
}

/** Anos que TÊM ciclo, do mais novo para o mais antigo. Pura. */
export function anosComCiclo(ciclos: readonly CicloRede[]): number[] {
  const anos = new Set<number>();
  for (const c of ciclos) {
    if (c.ano != null) {
      anos.add(c.ano);
    }
  }
  return [...anos].sort((a, b) => b - a);
}

/**
 * Ano a exibir por padrão: o ano CORRENTE quando ele já tem ciclo; senão o mais
 * recente que tenha — para o card nunca abrir vazio na virada do ano (ou antes
 * de o primeiro ciclo do ano ser aberto). `null` só quando não há ciclo nenhum.
 * `agora` é injetável para teste.
 */
export function anoVigente(anos: readonly number[], agora: number = Date.now()): number | null {
  if (anos.length === 0) {
    return null;
  }
  const atual = new Date(agora).getFullYear();
  return anos.includes(atual) ? atual : (anos[0] ?? null);
}

/* ---------------- Métricas da rede (KPIs da Início) ---------------- */

const CHAVES_SELO: readonly ChaveSelo[] = ["ouro", "prata", "bronze", "dev", "pend"];

/** Números do topo da Início, sobre um recorte de ciclos (normalmente um ano). */
export interface MetricasRede {
  /** Unidades DISTINTAS com ciclo no recorte (não o cadastro inteiro). */
  unidades: number;
  /** Ciclos no recorte (uma unidade com 2 visitas conta 2). */
  ciclos: number;
  /** Ciclos dentro da janela de edição. */
  abertos: number;
  /** Unidades cujo ciclo mais recente já tem IMGQ calculado. */
  comBase: number;
  /** Média do IMGQ com UMA unidade, UM voto — o ciclo mais recente dela. */
  mediaImgq: number | null;
  selos: Record<ChaveSelo, number>;
  /** ouro + prata + bronze. */
  selosPrevistos: number;
}

/** Selo conhecido do motor; qualquer outra coisa (ou nada) ⇒ pendente. */
function chaveSelo(selo: string | null): ChaveSelo {
  return CHAVES_SELO.includes(selo as ChaveSelo) ? (selo as ChaveSelo) : "pend";
}

/**
 * Agrega os KPIs da rede a partir dos ciclos exibidos. Uma unidade com 1ª e 2ª
 * visita entra UMA vez na média e na contagem de selos — pelo ciclo mais recente
 * —, senão a unidade mais visitada pesaria o dobro. Pura.
 */
export function metricasDaRede(ciclos: readonly CicloRede[]): MetricasRede {
  const recente = new Map<string, CicloRede>();
  for (const c of ciclos) {
    const atual = recente.get(c.unidadeId);
    if (!atual || (c.dataInicio ?? "") > (atual.dataInicio ?? "")) {
      recente.set(c.unidadeId, c);
    }
  }
  const selos: Record<ChaveSelo, number> = { ouro: 0, prata: 0, bronze: 0, dev: 0, pend: 0 };
  const notas: number[] = [];
  for (const c of recente.values()) {
    selos[chaveSelo(c.selo)]++;
    if (c.imgq != null) {
      notas.push(c.imgq);
    }
  }
  return {
    unidades: recente.size,
    ciclos: ciclos.length,
    abertos: ciclos.filter((c) => c.aberto).length,
    comBase: notas.length,
    mediaImgq: notas.length > 0 ? notas.reduce((a, b) => a + b, 0) / notas.length : null,
    selos,
    selosPrevistos: selos.ouro + selos.prata + selos.bronze,
  };
}

/* ---------------- Estado (fase) do ciclo em foco — stepper da Início ---------------- */

/** Índices de `CICLO.fases` — a ordem do seed é contratual (doc 16). */
export const FASE = {
  PRE_VISITA: 0,
  BASELINE: 1,
  MONITORAMENTO: 2,
  FINAL: 3,
  VALIDACAO: 4,
} as const;

export interface EstadoCiclo {
  /** Índice da fase corrente em `CICLO.fases`. `-1` ⇒ nenhum ciclo selecionado. */
  fase: number;
  /** "Ciclo 2026" com o ano REAL do ciclo; "Ciclo" quando ele não tem data. */
  titulo: string;
  /** Janela real: "Jun → Nov/2026". "" quando o ciclo não tem datas. */
  periodo: string;
}

/** "Jun → Nov/2026" a partir das datas reais. "" quando não há início. */
function periodoDoCiclo(inicio: string | null, fim: string | null): string {
  const mi = rotuloMes(inicio);
  if (!mi) {
    return "";
  }
  const my = mesAnoNum(fim ?? inicio);
  const mf = rotuloMes(fim ?? inicio);
  return `${mi} → ${mf}${my ? `/${my.ano}` : ""}`;
}

/**
 * Em que fase o ciclo em foco está, DE FATO — derivado do dado, não do
 * `faseAtual` fixo que vinha no seed de demonstração.
 *
 * A leitura, em ordem: escopo ainda não confirmado ⇒ pré-visita; 1ª visita
 * aberta ⇒ baseline; 1ª visita fechada e sem 2ª ⇒ monitoramento; 2ª visita (ou
 * além) aberta ⇒ visita final; fechada, o passo corrente é a validação AUDIN —
 * que também é onde o ciclo já validado repousa (o stepper não tem estado
 * "tudo concluído"). Pura; `agora` injetável para teste.
 */
export function estadoDoCiclo(
  avaliacoes: readonly AvaliacaoRemota[],
  avaliacaoId: string | null | undefined,
  escopoConfirmado: boolean,
  agora: number = Date.now(),
): EstadoCiclo {
  const av = avaliacaoId ? avaliacoes.find((a) => a.id === avaliacaoId) : undefined;
  if (!av) {
    return { fase: -1, titulo: "Ciclo", periodo: "" };
  }
  const ano = mesAnoNum(av.ciclo?.dataInicio)?.ano ?? null;
  const base = {
    titulo: ano ? `Ciclo ${ano}` : "Ciclo",
    periodo: periodoDoCiclo(av.ciclo?.dataInicio ?? null, av.ciclo?.dataFim ?? null),
  };
  const { i } = indiceNaUnidade(avaliacoes, av);
  const aberto = cicloAberto(av, agora);
  if (!escopoConfirmado) {
    return { ...base, fase: FASE.PRE_VISITA };
  }
  if (i <= 1) {
    return { ...base, fase: aberto ? FASE.BASELINE : FASE.MONITORAMENTO };
  }
  return { ...base, fase: aberto ? FASE.FINAL : FASE.VALIDACAO };
}
